<template>
  <div class="relative z-40" aria-labelledby="slide-over-title" role="dialog" aria-modal="true">
    <!-- Background backdrop, show/hide based on slide-over state. -->
    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity duration-200 pointer-events-none opacity-0" :class="show && 'pointer-events-auto !opacity-100'"></div>


    <div class="fixed inset-0 w-full h-full left-0 top-0 z-20 bg-gray-500 bg-opacity-75 transition-opacity duration-200 pointer-events-none opacity-0" :class="show && 'pointer-events-auto !opacity-100'"></div>
    <div class="fixed inset-0 overflow-hidden pointer-events-none z-30">
      <div class="absolute inset-0 overflow-hidden ">
        <div class="pointer-events-none fixed inset-y-0 right-0 flex pl-10 sm:pl-16">

          <transition
            enter-class="translate-x-full"
            leave-class="translate-x-0"
            enter-to-class="translate-x-0"
            leave-to-class="translate-x-full"
          >
            <div v-if="show" class="duration-200 pointer-events-auto w-screen max-w-5xl relative z-30">
            <form class="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
              <div class="flex-1">
                <!-- Header -->
                <div class="bg-gray-50 px-4 py-6 sm:px-6">
                  <div class="flex items-start justify-between space-x-3">
                    <div class="space-y-1">
                      <h2 class="text-lg font-medium text-gray-900" id="slide-over-title">
                        {{ editing ? "Update template" : "New template" }}
                      </h2>
                      <p class="text-sm text-gray-500">
                        {{
                          isAdmin
                            ? "As an admin, you can create default templates for determined packages. All customers who have access to the package will also be able to see and import its templates. Please be sure to save your changes before continuing."
                            : "We'll take this site's current config and save it on a template so that you can reuse it in the future. Please be sure to save your changes before continuing."
                        }}
                      </p>
                    </div>
                    <div class="flex h-7 items-center">
                      <button type="button" class="text-gray-400 hover:text-gray-500" @click="show = false">
                        <span class="sr-only">Close panel</span>
                        <!-- Heroicon name: outline/x -->
                        <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Divider container -->
                <div class="space-y-6 py-6 sm:space-y-0 sm:divide-y sm:divide-gray-200 sm:py-0">
                  <!-- Project name -->
                  <div class="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                    <div>
                      <label for="project-name" class="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"> Template name </label>
                    </div>
                    <div class="sm:col-span-2">
                      <input v-model.trim="$v.name.$model" ref="nameInput" type="text" name="project-name" id="project-name" :class="$v.name.$error && '!border-red-600'" class="block w-full rounded-md border focus:border-gray-300 border-gray-300 duration-200 outline-none shadow-sm focus:ring-primary sm:text-sm">
                      <span v-if="$v.name.$error" class="text-red-600">Name is required</span>
                    </div>
                  </div>

                  <!-- Project description -->
                  <div class="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                    <div>
                      <label for="project-description" class="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"> Description </label>
                    </div>
                    <div class="sm:col-span-2">
                      <textarea v-model.trim="$v.description.$model" :class="$v.description.$error && '!border-red-600'" id="project-description" name="project-description" rows="3" class="block w-full rounded-md border outline-none duration-200 focus:border-gray-300 border-gray-300 shadow-sm outline-none focus:ring-2 focus:ring-primary sm:text-sm"></textarea>
                      <span v-if="$v.description.$error" class="text-red-600">Description is required</span>
                    </div>
                  </div>

                  <!-- Package ID -->
                  <div v-if="isAdmin && $v.packageId" class="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                    <div>
                      <label for="project-name" class="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"> Package </label>
                    </div>
                    <div class="sm:col-span-2">
                      <select
                        v-model.trim="$v.packageId.$model"
                        type="text"
                        name="project-name"
                        id="project-name"
                        :class="$v.packageId.$error && '!border-red-600'"
                        class="block w-full rounded-md border focus:border-gray-300 border-gray-300 duration-200 outline-none shadow-sm focus:ring-primary sm:text-sm"

                        :disabled="editing"
                      >
                        <option
                          v-for="item in availablePackages"
                          :key="item.uid"

                          :value="item.uid"
                        >{{ item.name }}</option>
                      </select>
                      <span v-if="$v.packageId.$error" class="text-red-600">Package is required</span>
                    </div>
                  </div>

                  <!-- Configuration source  -->
                  <div v-if="!editing && isAdmin" class="space-y-1 px-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:space-y-0 sm:px-6 sm:py-5">
                    <div>
                      <label for="project-description" class="block text-sm font-medium text-gray-900 sm:mt-px sm:pt-2"> Source configuration </label>
                    </div>
                    <div class="sm:col-span-2">
                      <select
                        v-model.trim="$v.configSource.$model"
                        type="text"
                        name="project-name"
                        id="project-name"
                        class="block w-full rounded-md border focus:border-gray-300 border-gray-300 duration-200 outline-none shadow-sm focus:ring-primary sm:text-sm"
                      >
                        <option
                          v-for="item in sourceOptions"
                          :key="item.uid"

                          :value="item.uid"
                        >{{ item.name }}</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 px-6 py-5">
                      <label for="cover-photo" class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2">Screenshot preview</label>
                      <div class="mt-1 sm:col-span-2 sm:mt-0">
                        <div class="flex flex-col space-y-3 items-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6">
                          <div v-if="$v.previewSrc.$model">
                            <img :src="$v.previewSrc.$model" class="w-auto h-64 border border-gray-200 rounded-sm overflow-hidden">
                          </div>
                          <div class="flex justify-center">
                            <div class="space-x-3 text-center flex items-center cursor-pointer" @click="selectingScreenshot = true">
                              <svg class="mx-auto h-8 w-8 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                                <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                              </svg>
                              <h3 class="text-blue-700">Select template preview screenshot</h3>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Action buttons -->
              <div class="flex-shrink-0 border-t border-gray-200 px-4 py-5 sm:px-6">
                <div class="flex justify-end space-x-3">
                  <button type="button" class="rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2" @click="show = false">Cancel</button>
                  <button type="button" class="space-x-2 inline-flex justify-center rounded-md border border-transparent bg-blue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2" @click="save">
                    <svg v-if="loading" class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Save</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
          </transition>
        </div>
      </div>


      <transition
        enter-class="opacity-0"
        leave-class="opacity-100"
        enter-to-class="opacity-100"
        leave-to-class="opacity-0"
        mode="out-in"
      >
        <div v-if="show && selectingScreenshot" class="duration-200 fixed w-full h-full left-0 top-0 z-40 flex justify-center items-center pointer-events-auto" @click.self="selectingScreenshot = false">
          <div class="duration-300 border border-gray-200 bg-white shadow-lg z-40 rounded-lg">
            <ImageManager
              :website-uid="website.uid"
              class="pointer-events-auto"

              @close="selectingScreenshot = false"
              @set-value="selectScreenshot"
            />
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>

import { required } from "vuelidate/lib/validators"
import { mapGetters } from "vuex"

import ImageManager from "@/components/SideEditor/ContainerOptions/ImageManager"

export default {
  components: {
    ImageManager,
  },
  validations() {
    let validations = {}

    if (this.isAdmin) {
      validations = {
        name: { required },
        description: { required },
        packageId: { required },
        previewSrc: { },
      }
    } else {
      validations = {
        name: { required },
        description: { },
        packageId: { },
        previewSrc: { },
      }
    }

    if (!this.editing && this.isAdmin) {
      validations.configSource = {}
    }

    return validations
  },
  props: ['value', 'editing'],
  data() {
    return {
      show: this.value || false,
      loading: false,
      selectingScreenshot: false,

      description: "",
      name: "",
      packageId: "",
      previewSrc: "",
      configSource: "website",

      sourceOptions: [
        {
          name: "Use current website's configuration",
          uid: "website",
        },
        {
          name: "Use copied JSON from clipboard ",
          uid: "clipboard",
        },
      ],
    }
  },
  created() {
    if (this.show) this.selectFirstPackage()
  },
  methods: {
    selectScreenshot(src) {
      this.$v.previewSrc.$model = src
      this.selectingScreenshot = false
    },
    isValidConfig(encodedConfig) {
      try {
        const config = JSON.parse(decodeURIComponent(encodedConfig))
        if (!config.blocks_collection) return false
        return JSON.stringify(config)
      } catch (err) {
        return false
      }
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error) return;
      this.loading = true

      const payload = {
        name: this.name,
        description: this.description,
        preview_src: this.previewSrc,
      }

      try {
        if (this.configSource === 'clipboard') {
          payload.magento_config = this.isValidConfig(await navigator.clipboard.read())
          if (!payload.magento_config) {
            throw "The copied config is invalid, please copy it again."
          }
        } else {
          payload.magento_config = this.stringifiedConfig
        }

        if (this.editing) {
          if (this.isAdmin) {
            await this.axios.put(`/api/editor-packages/${this.packageId}/templates/${this.editing.template.uid}`, payload)
          } else {
            await this.axios.put(`/api/custom-templates/${this.editing.template.uid}`, payload)
          }
        } else {
          if (this.isAdmin) {
            await this.axios.post(`/api/editor-packages/${this.packageId}/templates`, payload)
          } else {
            await this.axios.post(`/api/custom-templates`, payload)
          }
        }

        await this.$store.dispatch("toast/success", { message: "Successfully created template!" })
        this.$emit("refetch-templates")
        this.show = false
      } catch (err) {
        await this.$store.dispatch("toast/handleError", err)
      } finally {
        this.loading = false
      }
    },
    async selectFirstPackage() {
      if (!this.$v.packageId.$model) {
        this.$v.packageId.$model = this.availablePackages[0].uid
      }
    },
  },
  computed: {
    ...mapGetters({
      stringifiedConfig: 'editor/stringifiedConfig',
      availablePackages: 'editor/availablePackages',
      website: 'editor/website',
      isAdmin: 'isAdmin',
      user: 'user',
    }),
  },
  watch: {
    show(val) {
      this.$v.name.$model = ""
      this.$v.description.$model = ""
      this.$v.previewSrc.$model = ""
      this.$v.packageId.$model = ""
      this.loading = false

      if (val) {
        setTimeout(() => this.$refs.nameInput.focus(), 210)

        if (this.editing) {
          this.$v.name.$model = this.editing.template.name
          this.$v.description.$model = this.editing.template.description
          this.$v.previewSrc.$model = this.editing.template.preview_src
          this.$v.packageId.$model = this.editing.editorPackage.uid
        }

        this.selectFirstPackage()
      }

      this.$v.$reset()
      this.$emit("input", val)
    },
    value(val) {
      this.show = val
    },
  },
}

</script>
